import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { ModeDossier } from './useApprenants';

export type TypeEntretien = 'CANDIDAT' | 'ENTREPRISE';

export type EtatEntretien =
  | 'PLANIFIE'
  | 'A_RELANCER'
  | 'REALISE'
  | 'RETENU'
  | 'NON_RETENU'
  | 'A_REVOIR';

/** Case à cocher de la fiche d'entretien, telle que l'API la décrit. */
export interface OptionFiche {
  code: string;
  libelle: string;
}

/**
 * Référentiel de la fiche d'entretien de recrutement, servi avec le tableau de
 * suivi. L'écran affiche des libellés qu'il ne connaît pas et enregistre des
 * codes : la liste ne vit qu'au backend.
 */
export interface ReferentielFiche {
  orienteurs: OptionFiche[];
  logements: OptionFiche[];
  situationsFamiliales: OptionFiche[];
  situationsActuelles: OptionFiche[];
  moyensLocomotion: OptionFiche[];
  allocationsCaf: OptionFiche[];
  aidesFranceTravail: OptionFiche[];
  couverturesSociales: OptionFiche[];
  objectifsFormation: OptionFiche[];
  outilsNumeriques: OptionFiche[];
  savoirsBase: OptionFiche[];
  contraintesImpact: OptionFiche[];
  conditionsTravail: OptionFiche[];
  disponibilites: OptionFiche[];
  etatsPermis: OptionFiche[];
  /** Structures vers lesquelles orienter un candidat non retenu. */
  orientations: OptionFiche[];
}

/** Réponse d'une case oui/non du papier. Vide : aucune des deux n'est cochée. */
export type ReponseOuiNon = '' | 'OUI' | 'NON';

/** Grille oui/non : une réponse par ligne, les lignes sans réponse absentes. */
export type GrilleOuiNon = Record<string, 'OUI' | 'NON'>;

/** Fiche d'entretien de recrutement (document du 16/07/2026). */
export interface FicheEntretien {
  /** Code du référentiel « orienté par », vide tant que rien n'est choisi. */
  orientePar: string;
  orienteParPrecision: string;
  formationDemandee: string;
  identite: {
    nomNaissance: string;
    prenoms: string;
    nomUsage: string;
    dateNaissance: string;
    communeNaissance: string;
    telephone: string;
    mail: string;
    nationalite: string;
    pieceIdentite: string;
    carteSejour: string;
    adresse: string;
    codePostal: string;
    ville: string;
  };
  logement: { statut: string; autre: string };
  familiale: {
    statut: string;
    nombreEnfants: string;
    agesEnfants: string;
    contactUrgence: string;
    responsableLegal: string;
    suiviJustice: ReponseOuiNon;
  };
  situation: {
    statuts: string[];
    autre: string;
    rqth: ReponseOuiNon;
    /** Posée seulement si `rqth` vaut « non ». */
    aideDossierRqth: ReponseOuiNon;
    aideDossierRqthCommentaire: string;
  };
  mobilite: {
    permis: string;
    permisPrecision: string;
    locomotion: string[];
    locomotionAutre: string;
    zoneDeplacement: string;
  };
  ressources: {
    allocationsCaf: string[];
    allocationAutre: string;
    franceTravail: ReponseOuiNon;
    aidesFranceTravail: string[];
    aideAutre: string;
    /** Posés seulement si `franceTravail` vaut « oui ». */
    conseillerFranceTravail: string;
    identifiantFranceTravail: string;
    missionLocale: ReponseOuiNon;
    conseillerMissionLocale: string;
    aah: ReponseOuiNon;
    couvertureSociale: string[];
  };
  parcours: {
    niveauFormation: string;
    dernierDiplome: string;
    derniereFormation: string;
    inscritCfa: ReponseOuiNon;
    cfaLequel: string;
  };
  experiences: {
    dernierEmploi: string;
    dejaTravailleDomaine: ReponseOuiNon;
    entreprise: string;
    fonction: string;
    duree: string;
    missions: string;
  };
  projet: {
    posteRecherche: string;
    motivation: string;
    objectifs: string[];
    objectifAutre: string;
  };
  competences: {
    cvAJour: ReponseOuiNon;
    pointsForts: ReponseOuiNon;
    pointsFortsPrecision: string;
    savoirs: GrilleOuiNon;
    numeriques: GrilleOuiNon;
    commentaires: string;
  };
  handicap: {
    situation: ReponseOuiNon;
    rencontrerReferent: ReponseOuiNon;
    besoins: string;
  };
  disponibilites: {
    disponibilite: string;
    dureeFormation: ReponseOuiNon;
    contraintesParticulieres: string;
    enqueteMetier: ReponseOuiNon;
    enquetePrecision: string;
    conditionsTravail: GrilleOuiNon;
    autresRemarques: string;
    contraintesImpact: GrilleOuiNon;
    precisionsContraintes: string;
  };
  conclusion: string;
  signatures?: {
    candidat?: string | null;
    chargeRecrutement?: string | null;
    dateSignature?: string;
  };
}

export const FICHE_VIDE: FicheEntretien = {
  orientePar: '',
  orienteParPrecision: '',
  formationDemandee: '',
  identite: {
    nomNaissance: '',
    prenoms: '',
    nomUsage: '',
    dateNaissance: '',
    communeNaissance: '',
    telephone: '',
    mail: '',
    nationalite: '',
    pieceIdentite: '',
    carteSejour: '',
    adresse: '',
    codePostal: '',
    ville: '',
  },
  logement: { statut: '', autre: '' },
  familiale: {
    statut: '',
    nombreEnfants: '',
    agesEnfants: '',
    contactUrgence: '',
    responsableLegal: '',
    suiviJustice: '',
  },
  situation: {
    statuts: [],
    autre: '',
    rqth: '',
    aideDossierRqth: '',
    aideDossierRqthCommentaire: '',
  },
  mobilite: {
    permis: '',
    permisPrecision: '',
    locomotion: [],
    locomotionAutre: '',
    zoneDeplacement: '',
  },
  ressources: {
    allocationsCaf: [],
    allocationAutre: '',
    franceTravail: '',
    aidesFranceTravail: [],
    aideAutre: '',
    conseillerFranceTravail: '',
    identifiantFranceTravail: '',
    missionLocale: '',
    conseillerMissionLocale: '',
    aah: '',
    couvertureSociale: [],
  },
  parcours: {
    niveauFormation: '',
    dernierDiplome: '',
    derniereFormation: '',
    inscritCfa: '',
    cfaLequel: '',
  },
  experiences: {
    dernierEmploi: '',
    dejaTravailleDomaine: '',
    entreprise: '',
    fonction: '',
    duree: '',
    missions: '',
  },
  projet: { posteRecherche: '', motivation: '', objectifs: [], objectifAutre: '' },
  competences: {
    cvAJour: '',
    pointsForts: '',
    pointsFortsPrecision: '',
    savoirs: {},
    numeriques: {},
    commentaires: '',
  },
  handicap: { situation: '', rencontrerReferent: '', besoins: '' },
  disponibilites: {
    disponibilite: '',
    dureeFormation: '',
    contraintesParticulieres: '',
    enqueteMetier: '',
    enquetePrecision: '',
    conditionsTravail: {},
    autresRemarques: '',
    contraintesImpact: {},
    precisionsContraintes: '',
  },
  conclusion: '',
  signatures: {
    candidat: null,
    chargeRecrutement: null,
    dateSignature: '',
  },
};

/**
 * Ce que le dossier du candidat sait déjà, servi avec l'entretien : la fiche
 * pré-remplit ses rubriques vides avec, au lieu de faire redonner au candidat
 * ce qu'il a donné à l'inscription. `mode` et `formationCode` sont les deux
 * seules valeurs que l'écran renvoie au dossier.
 */
export interface DossierEntretien {
  mode: ModeDossier;
  formationCode: string | null;
  mineur: boolean;
  identite: {
    nomNaissance: string;
    prenoms: string;
    nomUsage: string;
    dateNaissance: string;
    communeNaissance: string;
    nationalite: string;
    telephone: string;
    mail: string;
    adresse: string;
    codePostal: string;
    ville: string;
    pieceIdentite: string;
  };
  responsableLegal: string;
}

/** Orientation d'un candidat non retenu : codes de `orientations`, et la précision de « Autre ». */
export interface OrientationEntretien {
  structures: string[];
  autre: string;
}

/** « Mission Locale, Reconnect, Autre : CCAS », libellés pris au référentiel servi par l'API. */
export function libelleOrientation(
  orientation: OrientationEntretien,
  referentiel: Pick<ReferentielFiche, 'orientations'>,
): string {
  if (orientation.structures.length === 0) return 'Aucune structure proposée';
  return orientation.structures
    .map((code) => {
      const nom = referentiel.orientations.find((option) => option.code === code)?.libelle ?? code;
      return code === 'AUTRE' && orientation.autre ? `${nom} : ${orientation.autre}` : nom;
    })
    .join(', ');
}

export interface LigneEntretien {
  id: string;
  type: TypeEntretien;
  interlocuteur: string;
  apprenantId: string | null;
  entrepriseId: string | null;
  formation: string | null;
  objet: string | null;
  dateEntretien: string;
  charge: string | null;
  etat: EtatEntretien;
  etatLibelle: string;
  etatTon: string;
  compteRendu: string | null;
  besoinsRecueillis: string | null;
  fiche: FicheEntretien | null;
  /** Orientation d'un candidat non retenu ; `null` pour toute autre issue. */
  orientation: OrientationEntretien | null;
  /** Dossier du candidat ; `null` pour un entretien d'entreprise. */
  dossier: DossierEntretien | null;
  sites: string[];
  clos: boolean;
  /** Dernier ajout ou dernière modification : ordre par défaut du tableau. */
  modifieLe: string;
}

/** Ordre du tableau de suivi : dernier ajouté ou modifié en tête par défaut. */
export type TriEntretiens = 'RECENT' | 'ANCIEN';

export interface ReponseEntretiens {
  entretiens: LigneEntretien[];
  total: number;
  compteurs: {
    aVenir: number;
    candidats: number;
    entreprises: number;
    retenus: number;
    aRelancer: number;
    realises: number;
  };
  alertes: { regle: string; libelle: string; message: string; declencheeLe: string }[];
  chargesDisponibles: { id: string; nom: string }[];
  referentielFiche: ReferentielFiche;
}

export interface FiltresEntretiens {
  site: string;
  type?: TypeEntretien;
  etat?: EtatEntretien;
  tri?: TriEntretiens;
  recherche?: string;
}

export function useEntretiens(filtres: FiltresEntretiens) {
  const parametres = new URLSearchParams({ site: filtres.site });
  if (filtres.type) parametres.set('type', filtres.type);
  if (filtres.etat) parametres.set('etat', filtres.etat);
  if (filtres.tri) parametres.set('tri', filtres.tri);
  if (filtres.recherche) parametres.set('recherche', filtres.recherche);

  return useQuery({
    queryKey: ['entretiens', parametres.toString()],
    queryFn: () => appelApi<ReponseEntretiens>(`/entretiens?${parametres.toString()}`),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['entretiens'] });
  void client.invalidateQueries({ queryKey: ['apprenants'] });
}

export function usePlanifierEntretien() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: {
      apprenantId?: string;
      entrepriseId?: string;
      dateEntretien: string;
      objet: string;
      chargeId?: string;
    }) => appelApi<LigneEntretien>('/entretiens', { methode: 'POST', corps: entree }),
    onSuccess: () => invalider(client),
  });
}

export function useEnregistrerIssue() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      id: string;
      resultat?: 'REALISE' | 'RETENU' | 'NON_RETENU' | 'A_REVOIR';
      compteRendu?: string;
      besoinsRecueillis?: string;
      fiche?: FicheEntretien;
      /** Accompagne uniquement `resultat: 'NON_RETENU'`. */
      orientation?: OrientationEntretien;
    }) => {
      const { id, ...corps } = variables;
      return appelApi<LigneEntretien>(`/entretiens/${id}`, { methode: 'PATCH', corps });
    },
    onSuccess: () => invalider(client),
  });
}

/**
 * Adresse du compte-rendu PDF. Le jeton de session voyage dans un cookie, donc
 * le navigateur peut ouvrir cette adresse directement — pas de `fetch` ni de
 * `Blob` à gérer pour une simple prévisualisation.
 */
export function urlCompteRenduEntretien(id: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/entretiens/${id}/compte-rendu`;
}

/**
 * Verse le compte-rendu au dossier apprenant, comme pièce jointe.
 *
 * Distinct de la prévisualisation : le PDF est figé à cet instant et rejoint
 * les documents du dossier. Les listes du dossier sont invalidées pour que la
 * pièce apparaisse sans rechargement.
 *
 * L'API verse deux documents d'un coup — le compte-rendu et la fiche de suivi
 * qui l'accompagne — et rend l'identifiant des deux : celui qui veut ensuite
 * déposer l'exemplaire signé de l'un ou de l'autre sait où l'attacher.
 */
export function useVerserCompteRendu() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<{ documentId: string; intitule: string; ficheSuiviId?: string }>(
        `/entretiens/${id}/compte-rendu`,
        { methode: 'POST' },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      void client.invalidateQueries({ queryKey: ['dossier'] });
    },
  });
}
